import { useRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { LuMaximize2, LuEraser, LuCheck, LuX } from 'react-icons/lu';

// Canvas para capturar firma a mano alzada.
// En pantalla completa (13-oct-2026): en el celu, tocar la firma la abre en grande y apaisada.
// Si el celu está parado, el recuadro aparece girado para que lo des vuelta y firmes a lo ancho
// (funciona aunque tenga la rotación bloqueada). Al tocar "Listo" la firma vuelve al recuadro chico.
const TINTA = '#1C1917';

function trazar(ctx, x, y, grosor) {
    ctx.lineTo(x, y);
    ctx.strokeStyle = TINTA;
    ctx.lineWidth = grosor;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
}

/** Copia una imagen dentro de un canvas manteniendo la proporción (centrada). */
function pintarAjustado(canvas, src, alListo) {
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const k = Math.min(canvas.width / img.width, canvas.height / img.height);
        const w = img.width * k, h = img.height * k;
        ctx.drawImage(img, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
        alListo && alListo();
    };
    img.src = src;
}

/** Recorta lo dibujado (sin el blanco de alrededor) para que la firma no quede chiquita al achicarla. */
function recortar(canvas) {
    const ctx = canvas.getContext('2d');
    const { width: w, height: h } = canvas;
    const d = ctx.getImageData(0, 0, w, h).data;
    let x0 = w, y0 = h, x1 = -1, y1 = -1;
    for (let y = 0; y < h; y += 2) for (let x = 0; x < w; x += 2) {
        if (d[(y * w + x) * 4 + 3] > 0) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
    }
    if (x1 < 0) return null;
    const pad = 16;
    x0 = Math.max(0, x0 - pad); y0 = Math.max(0, y0 - pad); x1 = Math.min(w, x1 + pad); y1 = Math.min(h, y1 + pad);
    const out = document.createElement('canvas');
    out.width = x1 - x0; out.height = y1 - y0;
    out.getContext('2d').drawImage(canvas, x0, y0, out.width, out.height, 0, 0, out.width, out.height);
    return out.toDataURL('image/png');
}

// ── Firma en grande ───────────────────────────────────────────────────────────
function FirmaGrande({ label, valor, proporcion, onListo, onCerrar }) {
    const canvasRef = useRef(null);
    const cajaRef = useRef(null);
    const dibujando = useRef(false);
    const [vacio, setVacio] = useState(!valor);
    const [vista, setVista] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
    useEffect(() => {
        const r = () => setVista({ w: window.innerWidth, h: window.innerHeight });
        window.addEventListener('resize', r); window.addEventListener('orientationchange', r);
        const prev = document.body.style.overflow; document.body.style.overflow = 'hidden';
        return () => { window.removeEventListener('resize', r); window.removeEventListener('orientationchange', r); document.body.style.overflow = prev; };
    }, []);

    // Siempre apaisado: si la pantalla está parada, se gira el recuadro 90°
    const girado = vista.h > vista.w;
    const largo = Math.max(vista.w, vista.h) - 24;
    const corto = Math.min(vista.w, vista.h) - 24;
    const altoBarra = 52;
    const anchoCanvas = Math.round(largo);
    const altoCanvas = Math.round(Math.min(corto - altoBarra, largo / proporcion * 1.6));

    useEffect(() => {
        const c = canvasRef.current;
        if (c && valor) pintarAjustado(c, valor);
    }, [anchoCanvas, altoCanvas]); // eslint-disable-line react-hooks/exhaustive-deps

    // Pasa del toque en pantalla a la coordenada del canvas (contando el giro)
    const pos = (e) => {
        const c = canvasRef.current;
        const src = e.touches ? e.touches[0] : e;
        const r = c.getBoundingClientRect();
        let lx, ly;
        if (girado) {
            // rotate(90deg): el borde superior del canvas queda a la derecha
            lx = (src.clientY - r.top) / r.height * c.width;
            ly = (r.right - src.clientX) / r.width * c.height;
        } else {
            lx = (src.clientX - r.left) / r.width * c.width;
            ly = (src.clientY - r.top) / r.height * c.height;
        }
        return { x: lx, y: ly };
    };
    const empezar = (e) => { e.preventDefault(); dibujando.current = true; const ctx = canvasRef.current.getContext('2d'); const { x, y } = pos(e); ctx.beginPath(); ctx.moveTo(x, y); };
    const mover = (e) => { if (!dibujando.current) return; e.preventDefault(); const { x, y } = pos(e); trazar(canvasRef.current.getContext('2d'), x, y, 3); setVacio(false); };
    const terminar = () => { dibujando.current = false; };
    const limpiar = () => { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); setVacio(true); };
    const listo = () => onListo(vacio ? null : recortar(canvasRef.current));

    const ancho = largo, alto = altoCanvas + altoBarra;
    const estiloCaja = girado
        ? { width: ancho, height: alto, transform: 'translate(-50%, -50%) rotate(90deg)', left: '50%', top: '50%', position: 'absolute' }
        : { width: ancho, height: alto, transform: 'translate(-50%, -50%)', left: '50%', top: '50%', position: 'absolute' };

    return createPortal(
        <div className="fixed inset-0 z-[5000] bg-black/85 overflow-hidden touch-none" role="dialog" aria-modal="true" aria-label={label || 'Firma'}>
            <div ref={cajaRef} style={estiloCaja} className="flex flex-col rounded-2xl overflow-hidden bg-white shadow-2xl">
                <div className="h-[52px] shrink-0 flex items-center gap-2 px-3 bg-[#F5F3EF] border-b border-[#E8E5E0]">
                    <button type="button" onClick={onCerrar} aria-label="Cancelar" className="w-10 h-10 rounded-xl flex items-center justify-center text-[#57534E] active:bg-[#E8E5E0]"><LuX size={18} /></button>
                    <p className="flex-1 text-[15px] font-black text-[#1C1917] truncate">{label || 'Firma'}</p>
                    <button type="button" onClick={limpiar} className="h-10 px-3 rounded-xl text-[13px] font-bold text-[#57534E] inline-flex items-center gap-1.5 active:bg-[#E8E5E0]"><LuEraser size={15} /> Borrar</button>
                    <button type="button" onClick={listo} className="h-10 px-4 rounded-xl bg-[#C9341F] text-white text-[13px] font-black inline-flex items-center gap-1.5 active:scale-95"><LuCheck size={15} /> Listo</button>
                </div>
                <div className="relative flex-1">
                    <canvas ref={canvasRef} width={anchoCanvas} height={altoCanvas}
                        className="absolute inset-0 w-full h-full touch-none cursor-crosshair"
                        onMouseDown={empezar} onMouseMove={mover} onMouseUp={terminar} onMouseLeave={terminar}
                        onTouchStart={empezar} onTouchMove={mover} onTouchEnd={terminar} />
                    <div className="absolute bottom-10 left-8 right-8 border-b-2 border-dashed border-[#E8E5E0] pointer-events-none" />
                    {vacio && <span className="absolute inset-0 flex items-center justify-center text-[15px] text-[#A8A29E] pointer-events-none select-none">Firmá acá, a lo ancho</span>}
                </div>
            </div>
            {girado && (
                <p className="absolute left-0 right-0 bottom-3 text-center text-[12px] text-white/70 pointer-events-none">Girá el celu para firmar más cómodo</p>
            )}
        </div>,
        document.body,
    );
}

// ── Recuadro chico (el de siempre) ────────────────────────────────────────────
// aclaracion / onAclaracion (16-oct-2026): nombre y apellido de quien firma, centrado debajo.
// Sin aclaración una firma es un garabato que no identifica a nadie.
export const faltaAclaracion = (firma, aclaracion) => !!firma && !(aclaracion || '').trim();

export default function FirmaPad({ value, onChange, label, height = 110, aclaracion, onAclaracion }) {
    const canvasRef = useRef(null);
    const drawing   = useRef(false);
    const [isEmpty, setIsEmpty] = useState(!value);
    const [grande, setGrande] = useState(false);
    // En celu (dedo) se firma en grande; en la compu se puede firmar directo en el recuadro
    const tactil = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;

    // Carga imagen guardada al montar
    useEffect(() => {
        if (!value || !canvasRef.current) return;
        pintarAjustado(canvasRef.current, value, () => setIsEmpty(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const getPos = (e, canvas) => {
        const rect = canvas.getBoundingClientRect();
        const src  = e.touches ? e.touches[0] : e;
        return {
            x: (src.clientX - rect.left) * (canvas.width  / rect.width),
            y: (src.clientY - rect.top)  * (canvas.height / rect.height),
        };
    };

    const startDraw = useCallback(e => {
        e.preventDefault();
        drawing.current = true;
        const canvas = canvasRef.current;
        const ctx    = canvas.getContext('2d');
        const { x, y } = getPos(e, canvas);
        ctx.beginPath();
        ctx.moveTo(x, y);
    }, []);

    const draw = useCallback(e => {
        if (!drawing.current) return;
        e.preventDefault();
        const canvas = canvasRef.current;
        const { x, y } = getPos(e, canvas);
        trazar(canvas.getContext('2d'), x, y, 2);
        setIsEmpty(false);
    }, []);

    const stopDraw = useCallback(() => {
        if (!drawing.current) return;
        drawing.current = false;
        onChange?.(canvasRef.current.toDataURL('image/png'));
    }, [onChange]);

    const limpiar = () => {
        const canvas = canvasRef.current;
        canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
        setIsEmpty(true);
        onChange?.(null);
    };

    // Lo firmado en grande vuelve al recuadro chico (mismo tamaño de imagen que antes)
    const desdeGrande = (img) => {
        setGrande(false);
        if (!img) { limpiar(); return; }
        const c = canvasRef.current;
        pintarAjustado(c, img, () => { setIsEmpty(false); onChange?.(c.toDataURL('image/png')); });
    };

    const anchoCanvas = 560, altoCanvas = height * 2;
    return (
        <div className="flex flex-col gap-1">
            {label && (
                <span className="text-label font-semibold text-ink">
                    {label}
                </span>
            )}
            <div className="relative border border-chip rounded-lg bg-white overflow-hidden"
                 style={{ height }}>
                <canvas
                    ref={canvasRef}
                    width={anchoCanvas}
                    height={altoCanvas}
                    className="w-full h-full touch-none cursor-crosshair"
                    {...(tactil
                        ? { onClick: () => setGrande(true) }
                        : { onMouseDown: startDraw, onMouseMove: draw, onMouseUp: stopDraw, onMouseLeave: stopDraw,
                            onTouchStart: startDraw, onTouchMove: draw, onTouchEnd: stopDraw })}
                />
                {/* Línea guía */}
                <div className="absolute bottom-8 left-4 right-4 border-b border-dashed border-[#E8E5E0] pointer-events-none" />
                {isEmpty && (
                    <span className="absolute inset-0 flex items-center justify-center text-label text-muted pointer-events-none select-none">
                        {tactil ? 'Tocá para firmar en grande' : 'Firmar aquí'}
                    </span>
                )}
            </div>
            <div className="flex items-center justify-between">
                <button type="button" onClick={() => setGrande(true)}
                    className="text-[11px] font-bold text-secondary inline-flex items-center gap-1 active:scale-95">
                    <LuMaximize2 size={12} /> Firmar en grande
                </button>
                <button type="button" onClick={limpiar}
                    className="text-[10px] text-muted hover:text-[#D13A28] transition-colors">
                    Limpiar
                </button>
            </div>
            {onAclaracion && (
                <input type="text" value={aclaracion || ''} onChange={e => onAclaracion(e.target.value)} maxLength={120}
                    autoCapitalize="words" placeholder="Aclaración: nombre y apellido"
                    className={`w-full h-10 px-3 rounded-lg bg-chip text-body text-ink text-center font-bold outline-none placeholder:text-muted placeholder:font-normal ${faltaAclaracion(value, aclaracion) ? 'ring-2 ring-[#C9341F]/50' : ''}`} />
            )}
            {grande && (
                <FirmaGrande label={label} valor={isEmpty ? null : canvasRef.current?.toDataURL('image/png')}
                    proporcion={anchoCanvas / altoCanvas} onListo={desdeGrande} onCerrar={() => setGrande(false)} />
            )}
        </div>
    );
}
